// Mirrors the `PostStatus` enum in packages/db/prisma/schema.prisma. Kept as a
// literal union here (not imported from Prisma) so packages that don't depend
// on @repo/db can still consume the blog DTOs.
export type PostStatus = 'draft' | 'published';

// A post is public when status='published' AND publishedAt <= now(). There is
// no third `scheduled` status and no worker — a future `publishedAt` on an
// otherwise published row IS the scheduling. `status` alone lies to a human
// (a post held for next Tuesday reads `published`), so anything shown to a
// person must derive its visibility from both columns via this function, and
// never render the raw `status`.
export type PostVisibility = 'draft' | 'scheduled' | 'live';

export function resolvePostVisibility(
  post: { status: PostStatus; publishedAt: string | Date | null },
  now: Date = new Date(),
): PostVisibility {
  if (post.status !== 'published') return 'draft';
  // A published row with no publish instant is a draft, not "live" — nothing
  // ever flips it, because only the clock (comparing `publishedAt` to now)
  // moves a post from scheduled to live, and NULL <= now() is unknown in SQL,
  // so `publiclyVisible()` filters the row out of every public surface
  // forever. Calling that "live" would be a lie: nothing can reach it.
  if (post.publishedAt === null) return 'draft';
  const at = post.publishedAt instanceof Date ? post.publishedAt : new Date(post.publishedAt);
  return at.getTime() > now.getTime() ? 'scheduled' : 'live';
}

export interface BlogAuthorSummary {
  slug: string;
  name: string;
  role: string | null;
  avatarUrl: string | null;
}

export interface BlogPostSummary {
  slug: string;
  title: string;
  excerpt: string;
  coverUrl: string | null;
  tags: string[];
  publishedAt: string | null;
  // Drives the sitemap's `<lastmod>` — `publishedAt` never changes once set,
  // so a post edited after publishing would otherwise never signal a
  // recrawl to search engines.
  updatedAt: string;
  author: BlogAuthorSummary;
  readingMinutes: number;
}

export interface BlogPostDetail extends BlogPostSummary {
  bodyMd: string;
}

export interface BlogTagCount {
  tag: string;
  count: number;
}

export interface BlogPostPage {
  items: BlogPostSummary[];
  total: number;
  page: number;
  pageSize: number;
}

// Vietnamese-aware slugify: NFD-normalise to split base letters from
// combining marks, strip the marks, then fold đ/Đ explicitly — NFD
// normalisation drops the combining accents but NOT the stroke on đ, so
// 'Đặt' becomes 'Dat' only once the character itself is replaced.
export function slugifyVi(title: string): string {
  return title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritical marks
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120)
    .replace(/-+$/g, '');
}
